// Runs the mention search only while a picker is open. Mounting it on demand
// keeps the API client out of every composer that never sees an "@", and
// keeps the composer's own hook order fixed.
import { type ReactNode, useEffect, useState } from 'react';

import {
    type MentionCandidate,
    useReferenceSearch,
} from '@apps/web/features/phoebe_chat_v3/api/reference_search';

const SETTLE_DELAY_MS = 150;

export type MentionSearchProps = Readonly<{
    readonly organizationId: string;
    readonly organizationTimeZone: string;
    readonly mode: 'live' | 'sandbox';
    readonly outreachId?: string;
    readonly query: string;
    readonly onResults: (
        candidates: ReadonlyArray<MentionCandidate>,
        loading: boolean,
        outreachShiftsTruncated: boolean,
    ) => void;
    readonly onSettledNoMatches: () => void;
}>;

export function MentionSearch({
    organizationId,
    organizationTimeZone,
    mode,
    outreachId,
    query,
    onResults,
    onSettledNoMatches,
}: MentionSearchProps): ReactNode {
    // Each distinct prefix is its own request, so wait for typing to pause
    // rather than searching every keystroke. The first query runs at once.
    const [settledQuery, setSettledQuery] = useState(query);
    useEffect(() => {
        const handle = window.setTimeout(
            () => setSettledQuery(query),
            SETTLE_DELAY_MS,
        );
        return () => window.clearTimeout(handle);
    }, [query]);
    const { candidates, hasRequestFailed, isLoading, outreachShiftsTruncated } =
        useReferenceSearch({
            organizationId,
            organizationTimeZone,
            mode,
            outreachId,
            query: settledQuery,
            enabled: true,
        });
    useEffect(() => {
        const settled = settledQuery === query;
        onResults(
            settled ? candidates : [],
            !settled || isLoading,
            settled && outreachShiftsTruncated,
        );
    }, [
        candidates,
        isLoading,
        onResults,
        outreachShiftsTruncated,
        query,
        settledQuery,
    ]);
    useEffect(() => {
        if (
            settledQuery === query &&
            /\s/u.test(settledQuery) &&
            !isLoading &&
            !hasRequestFailed &&
            candidates.length === 0
        ) {
            onSettledNoMatches();
        }
    }, [
        candidates.length,
        hasRequestFailed,
        isLoading,
        onSettledNoMatches,
        query,
        settledQuery,
    ]);
    return null;
}
